import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { portalUserInitials } from '@/components/PortalPj/portalPjUi'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/utils/cn'

type Props = {
  userName?: string | null
  onLogout?: () => void
  onSearchClick?: () => void
  crmHomeHref?: string
  /** Página pública (cadastro PJ) — só logo, sem sessão */
  variant?: 'portal' | 'public'
}

/** Barra superior do portal com a identidade visual RocketSearch. */
export function PortalPjSupertopo({
  userName,
  onLogout,
  onSearchClick,
  crmHomeHref,
  variant = 'portal',
}: Props) {
  const isPublic = variant === 'public'
  const { theme } = useThemeStore()
  const logoSrc = '/logo-rocket-dark.png'
  const initials = portalUserInitials(userName)

  return (
    <div className='shrink-0'>
      <div className='border-b border-white/10 bg-[#0d0221]/95 backdrop-blur'>
        <div className='mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6'>
          <img
            src={logoSrc}
            alt={theme.companyName}
            className='h-7 w-auto object-contain'
            onError={(e) => {
              ;(e.target as HTMLImageElement).src = '/logo-sidebar-light.png'
            }}
          />
          <div className='flex items-center gap-1 sm:gap-2'>
            {!isPublic && crmHomeHref && (
              <Link
                to={crmHomeHref}
                className='hidden rounded-full px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:inline'
              >
                Voltar ao CRM
              </Link>
            )}
            {!isPublic && onSearchClick && (
              <button
                type='button'
                onClick={onSearchClick}
                className='flex h-10 w-10 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white'
                aria-label='Buscar solicitação'
              >
                <Search size={20} />
              </button>
            )}
            {!isPublic && (
              <>
                <span
                  className='flex h-9 w-9 items-center justify-center rounded-full bg-[#e91e63] text-xs font-semibold text-white shadow-lg shadow-[#e91e63]/30'
                  title={userName ?? 'Profissional'}
                >
                  {initials}
                </span>
                {onLogout && (
                  <button
                    type='button'
                    onClick={onLogout}
                    className='hidden text-sm font-medium text-white/70 hover:text-white sm:inline'
                  >
                    Sair
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className='relative h-32 w-full overflow-hidden sm:h-36'
        role='presentation'
        aria-hidden
      >
        <div className='absolute inset-0 bg-gradient-to-br from-[#0d0221] via-[#24104b] to-[#3a073d]' />
        <div
          className='absolute inset-0 opacity-[0.8]'
          style={{
            backgroundImage: `
              radial-gradient(circle at 12% 35%, rgba(255,255,255,0.9) 0 1px, transparent 2px),
              radial-gradient(circle at 72% 20%, rgba(255,255,255,0.75) 0 1px, transparent 2px),
              radial-gradient(circle at 84% 68%, rgba(255,255,255,0.65) 0 1px, transparent 2px),
              linear-gradient(90deg, transparent 0%, rgba(233, 30, 99, 0.2) 52%, transparent 100%)
            `,
            backgroundSize: '120px 120px, 180px 180px, 150px 150px, 100% 100%',
          }}
        />
        <div className='absolute -left-8 top-1/2 h-24 w-24 -translate-y-1/2 rounded-full bg-[#e91e63]/25 blur-2xl' />
        <div className='absolute right-[12%] top-1/4 h-20 w-20 rounded-full bg-white/10 blur-xl' />
        <svg
          className='absolute inset-0 h-full w-full opacity-[0.18]'
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='xMidYMid slice'
        >
          <defs>
            <pattern id='portal-circuit' width='80' height='80' patternUnits='userSpaceOnUse'>
              <path
                d='M0 40 H24 M56 40 H80 M40 0 V24 M40 56 V80 M24 40 a8 8 0 0 1 16 0'
                fill='none'
                stroke='rgb(233, 30, 99)'
                strokeWidth='0.75'
              />
            </pattern>
          </defs>
          <rect width='100%' height='100%' fill='url(#portal-circuit)' />
        </svg>
      </div>
    </div>
  )
}

export function PortalPjBreadcrumbs({
  className,
  crmHomeHref,
  currentLabel = 'Portal de solicitações',
}: {
  className?: string
  crmHomeHref?: string
  currentLabel?: string
}) {
  return (
    <nav aria-label='Breadcrumb' className={cn('text-sm text-text-muted', className)}>
      <ol className='flex flex-wrap items-center gap-1.5'>
        {crmHomeHref ? (
          <>
            <li>
              <Link to={crmHomeHref} className='font-medium text-white hover:text-[#e91e63]'>
                CRM
              </Link>
            </li>
            <li aria-hidden className='text-text-muted/60'>
              /
            </li>
          </>
        ) : (
          <li className='font-medium text-white'>RocketSearch</li>
        )}
        {!crmHomeHref && (
          <>
            <li aria-hidden className='text-text-muted/60'>
              /
            </li>
          </>
        )}
        <li className='text-[#e91e63]'>{currentLabel}</li>
      </ol>
    </nav>
  )
}
